// AI Chat Service - Angular WebSocket service for real-time chat

import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, firstValueFrom, timeout, catchError, of, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpService } from './../../../core/services/http/http.service';
export interface ChatMessage {
    id?: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    isStreaming?: boolean;
}

export interface Conversation {
    id: string;
    title: string;
    lastMessageAt: Date;
    createdAt: Date;
}

interface HttpChatResponse {
    conversationId: string;
    response: {
        response: string;
        sources: string[];
        confidence: number;
        contextFound: boolean;
    };
}

@Injectable({
    providedIn: 'root'
})
export class AiChatService implements OnDestroy {
    private socket: Socket | null = null;
    private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
    private typingSubject = new BehaviorSubject<boolean>(false);
    private connectedSubject = new BehaviorSubject<boolean>(false);
    private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
    private currentConversationId: string | null = null;
    private useHttpFallback = false;
    private connectionTimeout: any = null;

    // Observables
    messages$ = this.messagesSubject.asObservable();
    typing$ = this.typingSubject.asObservable();
    connected$ = this.connectedSubject.asObservable();
    conversations$ = this.conversationsSubject.asObservable();
    httpService = inject(HttpService)
    constructor(private http: HttpClient) { }

    get currentConversation(): string | null {
        return this.currentConversationId;
    }

    connect(): void {
        if (this.socket?.connected) return;
        if (this.useHttpFallback && this.connectedSubject.value) return;

        const token = localStorage.getItem('auth_token');
        if (!token) {
            console.warn('No auth token available for AI chat');
            // Enable HTTP fallback even without token for unauthenticated usage
            this.useHttpFallback = true;
            this.connectedSubject.next(true);
            return;
        }

        // Strip /api from the URL for WebSocket connection as Gateways don't use global prefix by default
        const baseUrl = environment.apiUrl.replace(/\/api\/?$/, '');

        this.socket = io(`${baseUrl}/ai-chat`, {
            auth: { token },
            transports: ['websocket'],
            reconnectionAttempts: 3,
            timeout: 10000,
        });

        // Enable HTTP fallback after 5 seconds if WebSocket doesn't connect
        this.connectionTimeout = setTimeout(() => {
            if (!this.connectedSubject.value) {
                console.log('WebSocket connection timeout, enabling HTTP fallback');
                this.useHttpFallback = true;
                this.connectedSubject.next(true);
            }
        }, 5000);

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            console.log('AI Chat connected');
            this.connectedSubject.next(true);
        });

        this.socket.on('disconnect', () => {
            console.log('AI Chat disconnected');
            this.connectedSubject.next(false);
        });

        this.socket.on('connected', (data: any) => {
            console.log('AI Chat authenticated:', data);
        });

        this.socket.on('typing', (data: { isTyping: boolean }) => {
            this.typingSubject.next(data.isTyping);
        });

        this.socket.on('message_chunk', (data: { chunk: string; conversationId: string }) => {
            this.handleChunk(data.chunk);
            this.currentConversationId = data.conversationId;
        });

        this.socket.on('message_complete', (data: { message: string; conversationId: string }) => {
            this.finalizeMessage(data.message);
            this.currentConversationId = data.conversationId;
        });

        this.socket.on('history', (data: { messages: any[]; conversationId: string }) => {
            const messages = data.messages.map((m: any) => ({
                role: m.role,
                content: m.content,
                timestamp: new Date(m.createdAt),
            }));
            this.messagesSubject.next(messages);
        });

        this.socket.on('conversations', (data: { conversations: Conversation[] }) => {
            this.conversationsSubject.next(data.conversations);
        });

        this.socket.on('error', (error: { message: string }) => {
            console.error('AI Chat error:', error.message);
        });
    }

    disconnect(): void {
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        this.socket?.disconnect();
        this.socket = null;
        this.connectedSubject.next(false);
        this.useHttpFallback = false;
    }

    sendMessage(message: string, language: string = 'en'): void {
        if (!message.trim()) return;

        // Add user message immediately
        const messages = this.messagesSubject.value;
        messages.push({
            role: 'user',
            content: message,
            timestamp: new Date(),
        });

        // Add placeholder for AI response
        messages.push({
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isStreaming: true,
        });

        this.messagesSubject.next([...messages]);

        // Check if we should use HTTP fallback or WebSocket
        if (this.useHttpFallback || !this.socket?.connected) {
            this.sendMessageViaHttp(message, language);
        } else {
            // Send via WebSocket
            this.socket.emit('send_message', {
                message,
                conversationId: this.currentConversationId,
                language,
            });
        }
    }

    private sendMessageViaHttp(message: string, language: string): void {
        this.typingSubject.next(true);

        this.httpService.post<HttpChatResponse>(`${environment.apiUrl}/ai-chat/message`, {
            message,
            conversationId: this.currentConversationId,
            language,
        }).pipe(
            timeout(120000), // 120 second timeout
            catchError(error => {
                console.error('HTTP chat error:', error);
                return of({
                    conversationId: this.currentConversationId || 'new',
                    response: {
                        response: "I'm sorry, I'm having trouble processing your request. Please try again later.",
                        sources: [],
                        confidence: 0,
                        contextFound: false,
                    }
                });
            })
        ).subscribe({
            next: (result) => {
                const messages = this.messagesSubject.value;
                const lastMessage = messages[messages.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                    lastMessage.content = result.response.response;
                    lastMessage.isStreaming = false;
                    this.messagesSubject.next([...messages]);
                }
                this.currentConversationId = result.conversationId;
                this.typingSubject.next(false);
            },
            error: (error) => {
                console.error('Chat error:', error);
                this.typingSubject.next(false);
            }
        });
    }

    sendVoiceMessage(audioBlob: Blob, language: string = 'en'): Observable<{ audio: Blob; transcript: string; response: string }> {
        this.typingSubject.next(true);

        // Determine the correct file extension based on MIME type
        const mimeToExt: Record<string, string> = {
            'audio/webm;codecs=opus': 'webm',
            'audio/webm': 'webm',
            'audio/ogg;codecs=opus': 'ogg',
            'audio/ogg': 'ogg',
            'audio/mp4': 'mp4',
            'audio/wav': 'wav',
            'audio/mpeg': 'mp3',
        };
        const ext = mimeToExt[audioBlob.type] || 'webm';
        const filename = `voice.${ext}`;

        console.log(`Sending voice: size=${audioBlob.size}, type=${audioBlob.type}, filename=${filename}, language=${language}`);

        const formData = new FormData();
        formData.append('audio', audioBlob, filename);
        formData.append('language', language);

        // Add placeholder for processing (language-aware)
        const isAr = language === 'ar';
        const messages = this.messagesSubject.value;
        messages.push({
            role: 'user',
            content: isAr ? '🎤 جاري التسجيل... (معالجة)' : '🎤 Recording... (processing)',
            timestamp: new Date(),
        });
        this.messagesSubject.next([...messages]);

        // Create headers with auth token - DO NOT set Content-Type, let browser set multipart boundary
        const token = localStorage.getItem('auth_token');
        let headers = new HttpHeaders();
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        // Using standard HttpClient (this.http) to ensure observe: 'response' works correctly
        return this.http.post(`${environment.apiUrl}/ai-chat/voice/conversation`, formData, {
            headers: headers,
            responseType: 'blob',
            observe: 'response',
        }).pipe(
            timeout(120000), // 120 seconds - voice processing can take time
            map(response => {
                const transcriptHeader = response.headers.get('X-Transcript') || '';
                const responseTextHeader = response.headers.get('X-Response-Text') || '';

                let transcript = transcriptHeader;
                let responseText = responseTextHeader;

                try { transcript = decodeURIComponent(transcriptHeader); } catch (e) { console.warn('Transcript decode error', e); }
                try { responseText = decodeURIComponent(responseTextHeader); } catch (e) { console.warn('Response decode error', e); }
                const audio = response.body as Blob;

                console.log('Voice Response:', {
                    status: response.status,
                    transcript,
                    responseText,
                    audioSize: audio?.size,
                    audioType: audio?.type,
                    headers: response.headers.keys()
                });

                // Update messages - find the user placeholder we added
                const currentMessages = this.messagesSubject.value;
                const isArabic = language === 'ar';
                // Find the last user message that says "Recording..." or "جاري التسجيل..."
                for (let i = currentMessages.length - 1; i >= 0; i--) {
                    if (currentMessages[i].role === 'user' && (currentMessages[i].content.includes('Recording...') || currentMessages[i].content.includes('جاري التسجيل'))) {
                        currentMessages[i].content = transcript || (isArabic ? '🎤 رسالة صوتية' : '🎤 Voice message');
                        break;
                    }
                }

                currentMessages.push({
                    role: 'assistant',
                    content: responseText || (isArabic ? '🔊 رد صوتي' : '🔊 Audio response'),
                    timestamp: new Date(),
                });

                this.messagesSubject.next([...currentMessages]);
                this.typingSubject.next(false);

                return {
                    audio,
                    transcript,
                    response: responseText
                };
            }),
            catchError(error => {
                console.error('Voice error:', error);
                this.typingSubject.next(false);
                const currentMessages = this.messagesSubject.value;

                // Update the placeholder to show failure
                for (let i = currentMessages.length - 1; i >= 0; i--) {
                    if (currentMessages[i].role === 'user' && (currentMessages[i].content.includes('Recording...') || currentMessages[i].content.includes('جاري التسجيل'))) {
                        currentMessages[i].content = language === 'ar' ? '🎤 رسالة صوتية (فشلت)' : '🎤 Voice message (failed)';
                        break;
                    }
                }

                currentMessages.push({
                    role: 'assistant',
                    content: language === 'ar' ? 'عذراً، لم أتمكن من معالجة رسالتك الصوتية. يرجى المحاولة مرة أخرى.' : 'Sorry, I could not process your voice message. Please try again.',
                    timestamp: new Date(),
                });
                this.messagesSubject.next([...currentMessages]);
                throw error;
            })
        );
    }

    private handleChunk(chunk: string): void {
        const messages = this.messagesSubject.value;
        const lastMessage = messages[messages.length - 1];

        if (lastMessage?.isStreaming) {
            lastMessage.content += chunk;
            this.messagesSubject.next([...messages]);
        }
    }

    private finalizeMessage(fullContent: string): void {
        const messages = this.messagesSubject.value;
        const lastMessage = messages[messages.length - 1];

        if (lastMessage?.isStreaming) {
            lastMessage.content = fullContent;
            lastMessage.isStreaming = false;
            this.messagesSubject.next([...messages]);
        }

        this.typingSubject.next(false);
    }

    loadHistory(conversationId: string): void {
        this.currentConversationId = conversationId;
        this.socket?.emit('get_history', { conversationId });
    }

    loadConversations(): void {
        this.socket?.emit('get_conversations');
    }

    newConversation(): void {
        this.currentConversationId = null;
        this.messagesSubject.next([]);
    }

    ngOnDestroy(): void {
        this.disconnect();
    }
}
